<template>
    <section class="text-card-section">
        <div class="text-card-section__container">
            <div class="text-card-section__inner">
                <!-- 左侧文字+按钮 -->
                <div class="text-card__left">
                    <template v-if="!hideTitle">
                        <h2 v-if="item.mainTitle" class="text-card__title">{{ item.mainTitle }}</h2>
                        <p v-if="item.subTitle" class="text-card__subtitle">{{ item.subTitle }}</p>
                    </template>
                    <button v-if="item.linkUrl || hasDefaultBtn" class="text-card__btn" @click="handleBtnClick">
                        {{ btnText }}
                    </button>
                </div>

                <!-- 右侧统计数据 -->
                <div v-if="fieldData.length > 0" class="text-card__stats">
                    <div v-for="(cell, idx) in fieldData" :key="String((cell as Record<string, unknown>)?.id ?? idx)"
                        class="text-card__stat">
                        <span class="text-card__stat-label">{{ getStatLabel(cell) }}</span>
                        <span class="text-card__stat-value">{{ getStatValue(cell) }}</span>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Module } from '@/api/page_config'
import { getStrWithRecord, jumpToUrl } from '.';

const props = withDefaults(defineProps<{
    item: Module
    hideTitle?: boolean
}>(), { hideTitle: false })

const emit = defineEmits<{
    (e: 'click', item: Module, cell?: unknown): void
}>()

const { t } = useI18n()

const fieldData = props.item.fieldData

const hasDefaultBtn = computed<boolean>(() => !!props.item.linkUrl)
const btnText = computed<string>(() => {
    return (props.item.linkText as string) || (props.item.linkText as string) || t('common.contactUs')
})

function getStatLabel(cell: unknown): string {
    return getStrWithRecord(cell, 'title')
}
function getStatValue(cell: unknown): string {
    return getStrWithRecord(cell, 'content')
}

function handleBtnClick(): void {
    emit('click', props.item)
    jumpToUrl(props.item.linkUrl);
}
</script>

<style scoped lang="scss">
@use '@/assets/styles/variables' as *;

$orange: #ff6d00;
$orange-dark: #e55d00;

.text-card-section {
    width: 100%;
}

.text-card-section__container {
    max-width: $container-max-width;
    margin: 0 auto;
    padding: 0 1rem;

    @media (min-width: $breakpoint-md) {
        padding: 0 2rem;
    }
}

.text-card-section__inner {
    display: flex;
    flex-direction: column;
    gap: 2rem;

    @media (min-width: $breakpoint-md) {
        flex-direction: row;
        align-items: flex-start;
        gap: 4rem;
    }
}

/* 左侧 */
.text-card__left {
    flex: 1;

    @media (min-width: $breakpoint-md) {
        flex: 0 0 auto;
        max-width: 380px;
        padding-top: 0.5rem;
    }
}

.text-card__title {
    font-size: 1.75rem;
    font-weight: 800;
    color: $color-text;
    margin: 0 0 0.5rem;
    line-height: 1.3;
    letter-spacing: -0.02em;

    @media (min-width: $breakpoint-md) {
        font-size: 2.25rem;
        margin-bottom: 0.75rem;
    }
}

.text-card__subtitle {
    font-size: 0.875rem;
    color: $color-text-secondary;
    margin: 0 0 1.5rem;
    line-height: 1.6;

    @media (min-width: $breakpoint-md) {
        font-size: 1rem;
        margin-bottom: 2rem;
    }
}

.text-card__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.625rem 1.5rem;
    background: $orange;
    color: #fff;
    border: none;
    border-radius: $radius-full;
    font-size: $font-size-sm;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s ease, transform 0.15s ease;

    @media (min-width: $breakpoint-md) {
        padding: 0.75rem 1.75rem;
        font-size: 0.875rem;
    }

    &:hover {
        background: $orange-dark;
        transform: scale(1.03);
    }

    &:active {
        transform: scale(0.98);
    }
}

/* 右侧统计 - 标签在上，数值在下 */
.text-card__stats {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem 2.5rem;
    flex: 1;

    @media (min-width: $breakpoint-md) {
        gap: 2rem 3.5rem;
    }
}

.text-card__stat {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.text-card__stat-label {
    font-size: 0.75rem;
    color: $color-text-secondary;
    font-weight: 500;

    @media (min-width: $breakpoint-md) {
        font-size: 0.8125rem;
    }
}

.text-card__stat-value {
    font-size: 1.5rem;
    font-weight: 800;
    color: $color-text;
    line-height: 1.2;
    letter-spacing: -0.02em;

    @media (min-width: $breakpoint-md) {
        font-size: 2rem;
    }
}
</style>
